import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import "../../components/skeletonRow.css";
import "../../whatsappChat/styles/whatsappChat.css";
import "../../whatsappChat/styles/inbox.css";
import "../../whatsappChat/styles/compose.css";
import "../../whatsappChat/styles/templates.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faCircleInfo,
  faDownload,
  faPaperclip,
  faSearch,
  faSpinner,
  faSync,
} from "@fortawesome/free-solid-svg-icons";
import { waChatClient, normalizeError } from "../../whatsappChat/api/client";
import { MessageBubble } from "../../whatsappChat/components/MessageBubble";
import { Modal } from "../../whatsappChat/components/Modal";
import { showToast } from "../../components/toast.jsx";
import { CHART_COLORS, ReportCharts, SummaryCards } from "./ReportCharts";

// BotSense chat message report: GET botsense-chat/report/messages (+ /export).

// One dropdown covers direction + status. Value is "<direction>:<status>"; both go to the API separately.
const STATUS_OPTIONS = [
  {
    label: "Sent messages",
    options: [
      { value: "outbound:", label: "All sent" },
      { value: "outbound:sent", label: "Sent" },
      { value: "outbound:delivered,read", label: "Delivered (incl. read)" },
      { value: "outbound:delivered", label: "Delivered, not read" },
      { value: "outbound:read", label: "Read" },
      { value: "outbound:failed", label: "Failed" },
    ],
  },
  {
    label: "Received messages",
    options: [{ value: "inbound:", label: "Received" }],
  },
  {
    label: "Everything",
    options: [{ value: "all:", label: "All messages" }],
  },
];

const statusOptionValue = ({ direction, status }) => {
  const value = `${direction || "outbound"}:${status || ""}`;
  const known = STATUS_OPTIONS.some((g) => g.options.some((o) => o.value === value));
  // e.g. a "received" card picked while showing everything
  return known ? value : `${direction || "outbound"}:`;
};

const PAGE_SIZE = 100;

// Cards, badges and charts share one palette.
const STATUS_COLORS = CHART_COLORS;

const STORAGE_KEY = "wa_report_filters";

// YYYY-MM-DD in IST, `daysBack` days before today.
const istDate = (daysBack = 0) =>
  new Date(Date.now() - daysBack * 86400000).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

const defaultFilters = () => ({
  from_date: istDate(6),
  to_date: istDate(0),
  number_id: "all",
  direction: "outbound",
  status: "",
  template_name: "",
  phone: "",
  page: 1,
  limit: PAGE_SIZE,
});

// DatePicker works with Date objects; filters keep YYYY-MM-DD strings.
const toDate = (value) => (value ? moment(value, "YYYY-MM-DD").toDate() : null);
const fromDate = (date) => (date ? moment(date).format("YYYY-MM-DD") : "");

const titleCase =(value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : "");

// "2026-10-19 11:02:10" -> "19-10-2026 11:02:10"
const formatDateTime = (value) => {
  const str = String(value || "").trim();
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}:\d{2}:\d{2})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]} ${m[4]}`;
  return str || "-";
};

// Only non-empty filters go to the API; page/limit only for the list.
const toParams = (filters, withPaging) => {
  const params = {};
  ["from_date", "to_date", "number_id", "direction", "status", "template_name", "phone"].forEach((key) => {
    const value = String(filters[key] ?? "").trim();
    if (value) params[key] = value;
  });
  if (withPaging) {
    params.page = filters.page;
    params.limit = filters.limit;
  }
  return params;
};

// A failed blob request carries its JSON error as a Blob.
const blobErrorMessage = async (err) => {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const json = JSON.parse(await data.text());
      const message = Array.isArray(json?.message) ? json.message.join(", ") : json?.message;
      if (message) return message;
    } catch {
      // not JSON
    }
  }
  return normalizeError(err).message;
};

// Plain-text message; older rows may not have message_text yet.
const rawText = (row) => row.message_text || row.content || "";

const URL_RE = /https?:\/\/[^\s"'<>]+/gi;
const IMAGE_RE = /\.(png|jpe?g|gif|webp|bmp)(\?|#|$)/i;
const VIDEO_RE = /\.(mp4|3gp|mov|webm)(\?|#|$)/i;
const FILE_RE = /\.(pdf|docx?|xlsx?|pptx?|csv|txt|zip|mp3|ogg|aac|amr|m4a)(\?|#|$)/i;

const mediaKind = (url) =>
  IMAGE_RE.test(url) ? "image" : VIDEO_RE.test(url) ? "video" : FILE_RE.test(url) ? "file" : null;

/**
 * Media links (header images, attachments) come inside message_text as bare URLs.
 * Returns the text without them plus the links, so the table shows text and the popup shows the media.
 */
const splitMedia = (row) => {
  const media = [];
  const add = (url, kind) => {
    if (url && kind && !media.some((m) => m.url === url)) media.push({ url, kind });
  };
  if (row.media?.link) add(row.media.link, mediaKind(row.media.link) || (row.type === "image" ? "image" : "file"));

  const text = rawText(row)
    .replace(URL_RE, (url) => {
      const kind = mediaKind(url) || (url === row.media?.link ? "file" : null);
      if (!kind) return url; // ordinary links stay in the text
      add(url, kind);
      return "";
    })
    .replace(/\[(image|video|document|audio)\]\s*/gi, "")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return { text, media };
};

// Same clean-up for a single string (text message content, template body).
const stripMediaUrls = (value) =>
  typeof value === "string"
    ? value.replace(URL_RE, (url) => (mediaKind(url) ? "" : url)).replace(/\n{3,}/g, "\n\n").trim()
    : value;

const MEDIA_LABEL = { image: "Image", video: "Video", file: "File" };

// Table cell: text without media URLs, 3 lines max. Clicking the row opens the popup with the media.
const MessageCell = ({ row }) => {
  const { text, media } = splitMedia(row);

  return (
    <td>
      {text ? (
        <div
          title={text}
          style={{
            maxWidth: "380px",
            whiteSpace: "pre-line",
            wordBreak: "break-word",
            fontSize: "12px",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {text}
        </div>
      ) : (
        <span className="text-muted font-italic">{media.length ? MEDIA_LABEL[media[0].kind] : "-"}</span>
      )}
    </td>
  );
};

// Support text for the info tooltip: "Error 131047: <details>"
const failureInfo = (failure) =>
  [failure?.code ? `Error ${failure.code}` : "", failure?.details || failure?.title || ""].filter(Boolean).join(": ");

const StatusBadge = ({ status }) => (
  <span
    style={{
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: "10px",
      fontSize: "11px",
      fontWeight: 600,
      color: "#fff",
      background: STATUS_COLORS[status] || "#b6c2c9",
    }}
  >
    {titleCase(status) || "-"}
  </span>
);

const FailureReason = ({ failure }) => {
  if (!failure) return null;
  const info = failureInfo(failure);
  return (
    <div className="text-danger" style={{ fontSize: "11px", maxWidth: "240px", whiteSpace: "normal", marginTop: "3px" }}>
      {failure.reason || failure.title || "WhatsApp did not give a reason for this failure"}
      {info && (
        <FontAwesomeIcon icon={faCircleInfo} title={info} style={{ marginLeft: "4px", cursor: "help", color: "#6c757d" }} />
      )}
    </div>
  );
};

/**
 * The message as it looks in WhatsApp Chat. Portalled to <body> so the chat
 * container's full-bleed rule (.content:has(.wa-chat-container)) does not hit this page.
 */
const MessagePopup = ({ row, onClose }) => {
  const { media } = splitMedia(row);
  // Media types already render their own link in the bubble.
  const extra = ["image", "video", "audio", "document", "sticker"].includes(row.type)
    ? media.filter((m) => m.url !== row.media?.link)
    : media;

  return createPortal(
    <div
      className="wa-chat-container"
      style={{ position: "fixed", inset: 0, width: "auto", height: "auto", margin: 0, border: 0, background: "transparent", zIndex: 1050 }}
    >
      <Modal title={`Message · ${row.phone || ""}`} badge={<StatusBadge status={row.status} />} onClose={onClose}>
        <div style={{ background: "var(--slate-50)", borderRadius: "8px", padding: "12px" }}>
          {extra.length > 0 && (
            <div className="d-flex flex-column" style={{ gap: "8px", marginBottom: "8px", alignItems: row.direction === "outbound" ? "flex-end" : "flex-start" }}>
              {extra.map((m) =>
                m.kind === "image" ? (
                  <a key={m.url} href={m.url} target="_blank" rel="noopener noreferrer" title="Open full size">
                    <img src={m.url} alt="Message media" style={{ maxWidth: "320px", maxHeight: "320px", borderRadius: "8px", border: "1px solid var(--border)", display: "block" }} />
                  </a>
                ) : m.kind === "video" ? (
                  <video key={m.url} src={m.url} controls preload="metadata" style={{ maxWidth: "320px", borderRadius: "8px" }} />
                ) : (
                  <a key={m.url} href={m.url} target="_blank" rel="noopener noreferrer">
                    <FontAwesomeIcon icon={faPaperclip} /> {decodeURIComponent(m.url.split("/").pop().split("?")[0]) || "Open file"}
                  </a>
                )
              )}
            </div>
          )}
          <MessageBubble
            message={{
              ...row,
              // The media is shown above, so drop its URL from the bubble text.
              ...(row.type === "text" ? { content: stripMediaUrls(row.content) } : {}),
              ...(row.template
                ? {
                    template: {
                      ...row.template,
                      header: stripMediaUrls(row.template.header) || null,
                      body: stripMediaUrls(row.template.body),
                    },
                  }
                : {}),
              message_at: row.sent_at,
              error: row.failure?.reason || row.error,
            }}
          />
        </div>
        <dl className="wa-meta wa-meta-after">
          <div>
            <dt>Sent at</dt>
            <dd>{formatDateTime(row.sent_at)}</dd>
          </div>
          <div>
            <dt>Business number</dt>
            <dd>{row.number?.label || row.number_id || "-"}</dd>
          </div>
          {row.template_name && (
            <div>
              <dt>Template</dt>
              <dd>{row.template_name}</dd>
            </div>
          )}
          <div>
            <dt>Sent by</dt>
            <dd>{row.sent_by_name || "-"}</dd>
          </div>
          {row.failure && (
            <>
              <div>
                <dt>Failure reason</dt>
                <dd>{row.failure.reason || "-"}</dd>
              </div>
              <div>
                <dt>Error code</dt>
                <dd>{row.failure.code || "-"}</dd>
              </div>
              <div>
                <dt>WhatsApp error details</dt>
                <dd style={{ whiteSpace: "pre-line" }}>{row.failure.details || "-"}</dd>
              </div>
            </>
          )}
        </dl>
      </Modal>
    </div>,
    document.body
  );
};

const WhatsappReport = () => {
  const [filters, setFilters] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      // page size is fixed; an older saved limit is ignored
      return saved ? { ...defaultFilters(), ...JSON.parse(saved), limit: PAGE_SIZE } : defaultFilters();
    } catch {
      return defaultFilters();
    }
  });
  const [formInputs, setFormInputs] = useState(filters);

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, by_status: {} });
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, total_pages: 1 });
  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [viewRow, setViewRow] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState("");
  const statsRequestRef = useRef(0);
  const requestRef = useRef(0);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    } catch {
      // storage unavailable
    }
  }, [filters]);

  useEffect(() => {
    waChatClient
      .get("/templates/numbers")
      .then((res) => setNumbers(res.data?.data || []))
      .catch(() => setNumbers([]));
  }, []);

  const fetchReport = async () => {
    const requestId = ++requestRef.current;
    setLoading(true);
    setError("");
    try {
      const res = await waChatClient.get("/report/messages", { params: toParams(filters, true) });
      if (requestId !== requestRef.current) return;
      setRows(res.data?.data || []);
      setSummary(res.data?.summary || { total: 0, by_status: {} });
      setPagination(res.data?.pagination || { page: 1, limit: filters.limit, total: 0, total_pages: 1 });
    } catch (err) {
      if (requestId !== requestRef.current) return;
      setRows([]);
      setSummary({ total: 0, by_status: {} });
      setPagination({ page: 1, limit: filters.limit, total: 0, total_pages: 1 });
      setError(normalizeError(err).message);
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Chart data covers the whole filter, so paging does not refetch it.
  const statsKey = JSON.stringify(toParams(filters, false));
  const fetchStats = async () => {
    const requestId = ++statsRequestRef.current;
    setStatsLoading(true);
    setStatsError("");
    try {
      const res = await waChatClient.get("/report/messages/stats", { params: JSON.parse(statsKey) });
      if (requestId !== statsRequestRef.current) return;
      // keep the API's "filters" (dates actually used) next to the chart data
      setStats(res.data?.data ? { ...res.data.data, filters: res.data.filters } : null);
    } catch (err) {
      if (requestId !== statsRequestRef.current) return;
      setStats(null);
      setStatsError(normalizeError(err).message);
    } finally {
      if (requestId === statsRequestRef.current) setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statsKey]);

  const handleInput = (e) => {
    const { name, value } = e.target;
    setFormInputs((prev) => ({ ...prev, [name]: value }));
  };

  // Dropdowns and dates apply at once; text boxes on Enter or Search.
  const handleSelect = (e) => {
    const { name, value } = e.target;
    setFormInputs((prev) => ({ ...prev, [name]: value }));
    setFilters((prev) => ({ ...prev, ...formInputs, [name]: value, page: 1 }));
  };

  const applyFilters = () => setFilters((prev) => ({ ...prev, ...formInputs, page: 1 }));

  const handleStatusOption = (e) => {
    const [direction, status = ""] = e.target.value.split(":");
    setFormInputs((prev) => ({ ...prev, direction, status }));
    setFilters((prev) => ({ ...prev, ...formInputs, direction, status, page: 1 }));
  };

  // One picker for the range; sent to the API as from_date / to_date (YYYY-MM-DD).
  // Applies once both ends are picked, or when cleared (the API then uses the last 7 days).
  const handleDateRangeChange = ([start, end]) => {
    const next = { from_date: fromDate(start), to_date: fromDate(end) };
    setFormInputs((prev) => ({ ...prev, ...next }));
    if ((start && end) || (!start && !end)) {
      setFilters((prev) => ({ ...prev, ...formInputs, ...next, page: 1 }));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") applyFilters();
  };

  const clearFilters = () => {
    const fresh = defaultFilters();
    setFormInputs(fresh);
    setFilters(fresh);
  };

  // Card click: filter sent messages by that status; the same card again (or Total) clears it.
  const pickStatus = (status) => {
    const value = !status || filters.status === status ? "" : status;
    const direction = value ? "outbound" : filters.direction;
    setFormInputs((prev) => ({ ...prev, status: value, direction }));
    setFilters((prev) => ({ ...prev, ...formInputs, status: value, direction, page: 1 }));
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= (pagination.total_pages || 1)) {
      setFilters((prev) => ({ ...prev, page }));
    }
  };

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await waChatClient.get("/report/messages/export", {
        params: toParams(filters, false),
        responseType: "blob",
      });
      const disposition = res.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      const fileName = match
        ? decodeURIComponent(match[1])
        : `whatsapp-messages_${filters.from_date}_to_${filters.to_date}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showToast.error(await blobErrorMessage(err));
    } finally {
      setExporting(false);
    }
  };

  const total = pagination.total || 0;
  const start = total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const end = Math.min(pagination.page * pagination.limit, total);
  const totalPages = pagination.total_pages || 1;

  const isDefault = (() => {
    const d = defaultFilters();
    return ["from_date", "to_date", "number_id", "direction", "status", "template_name", "phone"].every(
      (key) => String(filters[key] ?? "") === String(d[key] ?? "")
    );
  })();

  return (
    <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
      <div id="content" className="">
        <div className="">
          {/* Summary cards: counts for the whole filter, not just this page. Click one to filter the table. */}
          <SummaryCards summary={summary} loading={loading} activeStatus={filters.status} onPick={pickStatus} />

          <ReportCharts
            stats={stats}
            statsError={statsError}
            statsLoading={statsLoading}
            summary={summary}
            loading={loading}
          />

          <div className="panel-heading d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center" style={{ gap: "6px" }}>
              {!isDefault && (
                <button
                  type="button"
                  className="btn btn-default btn-xs"
                  style={{ marginRight: "10px" }}
                  onClick={clearFilters}
                  title="Back to the last 7 days, all numbers"
                >
                  Clear Filter
                </button>
              )}
            </div>
            <div>
              <span style={{ fontSize: "0.8rem" }} className="me-3">
                {loading ? (
                  <span className="skeleton-text" style={{ width: "140px", display: "inline-block" }}></span>
                ) : (
                  `${start} to ${end} of ${total}`
                )}
              </span>
              <button className="btn btn-default btn-sm mx-1" onClick={() => { fetchReport(); fetchStats(); }} title="Refresh" disabled={loading}>
                <FontAwesomeIcon icon={faSync} spin={loading} />
              </button>
              <button className="btn btn-default btn-sm mx-1" onClick={handleExport} disabled={exporting || total === 0}>
                <FontAwesomeIcon icon={exporting ? faSpinner : faDownload} spin={exporting} /> {exporting ? "Exporting…" : "Export Excel"}
              </button>
              <button
                className={`btn btn-default btn-sm ${pagination.page <= 1 ? "d-none" : ""}`}
                title="Previous"
                onClick={() => handlePageChange(pagination.page - 1)}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              &nbsp;
              <button
                className={`btn btn-default btn-sm ${pagination.page >= totalPages ? "d-none" : ""}`}
                title="Next"
                onClick={() => handlePageChange(pagination.page + 1)}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>

          <div className="panel panel-default with-rounded-corner with-shadow">
            <div style={{ overflow: "auto", height: "calc(100vh - 330px)", minHeight: "300px", position: "relative" }}>
              <table className="table table-condensed table-hover bg-white sticky-table">
                <thead>
                  <tr>
                    <th>Sent At</th>
                    <th>Mobile</th>
                    <th>Business Number</th>
                    <th>Type / Template</th>
                    <th style={{ minWidth: "260px" }}>Message</th>
                    <th>Status</th>
                    <th>Sent By</th>
                    <th>Status Updated</th>
                  </tr>
                  <tr>
                    <th>
                      <div className="d-flex align-items-center gap-1">
                        <DatePicker
                          selected={toDate(formInputs.from_date)}
                          onChange={handleDateRangeChange}
                          startDate={toDate(formInputs.from_date)}
                          endDate={toDate(formInputs.to_date)}
                          selectsRange
                          isClearable
                          toggleCalendarOnIconClick
                          placeholderText="Last 7 days"
                          monthsShown={2}
                          showYearDropdown
                          showMonthDropdown
                          dropdownMode="select"
                          maxDate={new Date()}
                          dateFormat="dd-MM-yyyy"
                        />
                        <button type="button" className="btn btn-default btn-xs btn-group-xs" onClick={applyFilters} title="Search">
                          <FontAwesomeIcon icon={faSearch} />
                        </button>
                      </div>
                    </th>
                    <th>
                      <input
                        type="text"
                        name="phone"
                        inputMode="tel"
                        placeholder="Search mobile..."
                        value={formInputs.phone}
                        onChange={handleInput}
                        onKeyDown={handleKeyDown}
                        style={{ width: "120px", padding: "5px" }}
                      />
                    </th>
                    <th>
                      <select name="number_id" value={formInputs.number_id} onChange={handleSelect} style={{ width: "160px", padding: "5px" }}>
                        <option value="all">All numbers</option>
                        {numbers.map((n) => (
                          <option key={n.number_id} value={n.number_id}>
                            {n.label || n.name || n.number_id}
                          </option>
                        ))}
                      </select>
                    </th>
                    <th>
                      <input
                        type="text"
                        name="template_name"
                        placeholder="Template name..."
                        value={formInputs.template_name}
                        onChange={handleInput}
                        onKeyDown={handleKeyDown}
                        style={{ width: "130px", padding: "5px" }}
                      />
                    </th>
                    <th><p>&nbsp;</p></th>
                    <th>
                      <select
                        value={statusOptionValue(formInputs)}
                        onChange={handleStatusOption}
                        style={{ width: "150px", padding: "5px" }}
                        title="Sent messages by status, or received messages"
                      >
                        {STATUS_OPTIONS.map((group) => (
                          <optgroup key={group.label} label={group.label}>
                            {group.options.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </th>
                    <th><p>&nbsp;</p></th>
                    <th><p>&nbsp;</p></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={`skeleton-${i}`} className="skeleton-row">
                        {Array.from({ length: 8 }).map((__, j) => (
                          <td key={j}><div className="skeleton-text" style={{ width: "75%" }}></div></td>
                        ))}
                      </tr>
                    ))
                  ) : error ? (
                    <tr>
                      <td colSpan={8} className="text-center text-danger py-4">{error}</td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center text-muted py-4">No messages match these filters.</td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr
                        key={row.id}
                        tabIndex={0}
                        title="Click to preview the message"
                        style={{ cursor: "pointer" }}
                        onClick={() => setViewRow(row)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") setViewRow(row);
                        }}
                      >
                        <td style={{ whiteSpace: "nowrap" }}>{formatDateTime(row.sent_at)}</td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          <div>{row.phone || "-"}</div>
                          {row.contact_name && <div className="text-muted" style={{ fontSize: "11px" }}>{row.contact_name}</div>}
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {row.number ? (
                            <>
                              <div>{row.number.name || "-"}</div>
                              <div className="text-muted" style={{ fontSize: "11px" }}>{row.number.phone ?? row.number.number_id}</div>
                            </>
                          ) : (
                            <span className="text-muted">{row.number_id || "-"}</span>
                          )}
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          <div>{titleCase(row.type) || "-"}</div>
                          {row.template_name && <div className="text-muted" style={{ fontSize: "11px" }}>{row.template_name}</div>}
                        </td>
                        <MessageCell row={row} />
                        <td style={{ whiteSpace: "nowrap" }}>
                          <StatusBadge status={row.status} />
                          {row.status === "failed" && <FailureReason failure={row.failure} />}
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>{row.sent_by_name || <span className="text-muted">-</span>}</td>
                        <td style={{ whiteSpace: "nowrap" }}>{formatDateTime(row.status_updated_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {viewRow && <MessagePopup row={viewRow} onClose={() => setViewRow(null)} />}
    </div>
  );
};

export default WhatsappReport;
